import { cn } from "../../lib/utils";
import { motion } from "framer-motion";

interface SpinnerProps {
  className?: string;
  size?: "sm" | "md" | "lg";
}

const bars = [
  { 
    id: 1, squares: 3, delay: 0,
    color: "#00FFFF",  // Cyan (I)
    lightColor: "#80FFFF",
    darkColor: "#009999"
  },
  { 
    id: 2, squares: 4, delay: 0.15,
    color: "#0000FF",  // Blue (J)
    lightColor: "#4D4DFF",
    darkColor: "#000099"
  },
  { 
    id: 3, squares: 2, delay: 0.3,
    color: "#FFA500",  // Orange (L)
    lightColor: "#FFD380",
    darkColor: "#996300"
  },
  { 
    id: 4, squares: 5, delay: 0.45,
    color: "#FFFF00",  // Yellow (O)
    lightColor: "#FFFF80",
    darkColor: "#999900"
  },
  { 
    id: 5, squares: 3, delay: 0.6,
    color: "#00FF00",  // Green (S)
    lightColor: "#80FF80",
    darkColor: "#009900"
  },
  { 
    id: 6, squares: 2, delay: 0.75,
    color: "#800080",  // Purple (T)
    lightColor: "#B366B3",
    darkColor: "#4D004D"
  },
  { 
    id: 7, squares: 4, delay: 0.9,
    color: "#FF0000",  // Red (Z)
    lightColor: "#FF6666",
    darkColor: "#990000"
  },
  { 
    id: 8, squares: 3, delay: 1.05,
    color: "#00FFFF",  // Cyan (I)
    lightColor: "#80FFFF",
    darkColor: "#009999"
  }
];

const MiniSquare = ({ color, lightColor, darkColor }: { color: string, lightColor: string, darkColor: string }) => (
  <div 
    className="w-3 h-3"
    style={{
      backgroundColor: color,
      boxShadow: `
        inset 1px 1px 0 ${lightColor},
        inset -1px -1px 0 ${darkColor}
      `
    }}
  />
);

const Square = ({ color, lightColor, darkColor }: { color: string, lightColor: string, darkColor: string }) => (
  <div className="grid grid-cols-2 w-6 h-6">
    <MiniSquare color={color} lightColor={lightColor} darkColor={darkColor} />
    <MiniSquare color={color} lightColor={lightColor} darkColor={darkColor} />
    <MiniSquare color={color} lightColor={lightColor} darkColor={darkColor} />
    <MiniSquare color={color} lightColor={lightColor} darkColor={darkColor} />
  </div>
);

const Spinner = ({ className, size = "md" }: SpinnerProps) => {
  const sizeClasses = {
    sm: "h-16",
    md: "h-24",
    lg: "h-32"
  };

  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        "flex items-center justify-center",
        className
      )}
    >
      <div className={cn("flex items-end gap-0", sizeClasses[size])}>
        {bars.map(bar => (
          <motion.div
            key={bar.id}
            className="flex flex-col-reverse overflow-hidden"
            initial={{ height: 0 }}
            animate={{ height: `${(bar.squares / 5) * 100}%` }}
            transition={{
              duration: 1.2,
              delay: bar.delay,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: [0.4, 0, 0.2, 1]
            }}
          >
            {[...Array(bar.squares)].map((_, i) => (
              <Square
                key={i}
                color={bar.color}
                lightColor={bar.lightColor}
                darkColor={bar.darkColor}
              />
            ))}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Spinner; 